import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import {
  Arrow,
  Button,
  Image,
  Link,
  Spacer,
  TranslationProvider,
  utils,
  useAnalytics,
} from '@resy/ui-kitchen';

import { VENUE_PAGE } from '../../../../../ui-kitchen/src/utils/getPageContext';

import Close from '../../svg/dynamic/close.svg';
import ExclusiveImage from '../../images/amexrga/benefit1_160_160.png';
import PriorityImage from '../../images/amexrga/benefit2_160_160.png';
import SpecialImage from '../../images/amexrga/benefit3_160_160.png';
import VIPImage from '../../images/amexrga/benefit4_160_160.png';
import ResyGdaLogo from '../../images/amexrga/ResyGDAExclusivelyForAmex.svg';
import ResyGdaLogoMobile from '../../images/amexrga/ResyGDAExclusivelyForAmex--Mobile.svg';

import './GdaOnboarding.scss';
import { selectIsGlobalDiningAccess } from '../../../../../ui-kitchen/src/reducers/userSlice';

const copy = {
  exclusive: {
    copyText: 'Primetime tables are being held just for you. Look for the purple reservations within the Resy app or resy.com.',
    copyKey: 'onboarding_exclusive_reservations_copy',
    headerText: 'Exclusive Reservations',
    headerKey: 'onboarding_exclusive_reservations_header',
    image: ExclusiveImage,
  },
  priorityNotify: {
    copyText: 'Set a notify and be among the first to know when a hard-to-get table opens up.',
    copyKey: 'onboarding_priority_notify_copy',
    headerText: 'Priority Notify',
    headerKey: 'onboarding_priority_notify_header',
    image: PriorityImage,
  },
  specialAccess: {
    copyText: 'Receive early access to in-person and virtual dining experiences with some of the best chefs and restaurants across the globe.',
    copyKey: 'onboarding_special_access_copy',
    headerText: 'Special Access to Events',
    headerKey: 'onboarding_special_access_header',
    image: SpecialImage,
  },
  vipStatus: {
    copyText: 'Restaurants will know that you are a valued guest by a special GDA badge in your Resy profile.',
    copyKey: 'onboarding_vip_status_copy',
    headerText: 'A Badge on Your Resy Profile',
    headerKey: 'onboarding_vip_status_title_2',
    image: VIPImage,
  },
};

const platinumCardImg = 'https://s3.amazonaws.com/resy.com/images/cards/amex-platinum-with-name.png';

const InfoCard = ({
  copyText,
  copyKey,
  headerText,
  headerKey,
  image,
}) => (
  <div className="GdaOnboarding__InfoCard">
    <Image src={image} className="GdaOnboarding__Image" />
    <Spacer width="15" />
    <div>
      <TranslationProvider translationKey={headerKey}>
        <h3 className="GdaOnboarding__InfoCard-header">{headerText}</h3>
      </TranslationProvider>
      <TranslationProvider translationKey={copyKey}>
        <p className="GdaOnboarding__InfoCard-copy">{copyText}</p>
      </TranslationProvider>
    </div>
  </div>
);

InfoCard.propTypes = {
  copyText: PropTypes.string.isRequired,
  copyKey: PropTypes.string.isRequired,
  headerText: PropTypes.string.isRequired,
  headerKey: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
};

const Exclusive = () => (
  <InfoCard {...copy.exclusive} />
);

const PriorityNotify = () => (
  <InfoCard {...copy.priorityNotify} />
);

const SpecialAccess = () => (
  <InfoCard {...copy.specialAccess} />
);

const VIPStatus = () => (
  <InfoCard {...copy.vipStatus} />
);

const LinkList = ({
  children,
}) => (
  <div className="LinkList">{children}</div>
);

LinkList.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf([
      PropTypes.node,
    ]),
  ]).isRequired,
};

const LinkRow = ({
  children,
  href,
  ...props
}) => (
  <div className="LinkRow">
    <Link href={href} {...props}>
      {children}
      <Arrow className="LinkRow__Arrow" />
    </Link>
  </div>
);

LinkRow.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  href: PropTypes.string.isRequired,
};

const GdaOnboarding = ({
  closeModal,
  hideCta,
  isGdaUser,
  isMobile,
}) => {
  const Analytics = useAnalytics();

  const isGdaFromMobile = utils.getQueryVariable('isGdaUser');
  const isGda = isGdaUser || isGdaFromMobile;
  const isViewingAccountPageFromMobile = utils.getQueryVariable('isLoggedIn');
  const pageContext = utils.getPageContext(window.location.pathname);
  const isGdaOnboardingRoute = window.location.pathname.startsWith('/global-dining-access-onboarding/');

  const showGdaAccountPageCopy = isGdaFromMobile ? (isGdaFromMobile && isViewingAccountPageFromMobile) : (isGdaUser && pageContext !== VENUE_PAGE);
  const showGdaVenuePageCopy = isGdaFromMobile || (isGdaUser && pageContext === VENUE_PAGE);

  useEffect(() => {
    //  While loading this page inside mobile apps, the "Skip to Main Content" button is not needed and creates confusion as it doesn't work.
    //  iOS screen reader announces the hidden button and it is against iOS policies to have a focusable element that is hidden,
    //  so we are setting display none instead of using aria-hidden.
    let skipButton;
    let previousDisplay;
    if (isGdaOnboardingRoute) {
      skipButton = document.getElementById('skip-to-main');
      if (skipButton) {
        previousDisplay = skipButton.style.display;
        skipButton.style.display = 'none';
      }
    }

    return () => {
      if (skipButton) {
        skipButton.style.display = previousDisplay;
      }
    };
  }, []);

  const renderHeader = () => (
    isGda ? (
      <TranslationProvider translationKey="onboarding_copy_member">
        <div className="GdaOnboarding__Subheader">As an American Express Global Dining Access member you&apos;ve unlocked access to additional reservations and will be first in line for Notify. Terms Apply.</div>
      </TranslationProvider>
    ) : (
      <TranslationProvider translationKey="onboarding_copy_prospect">
        <div className="GdaOnboarding__Subheader">Unlock access to exclusive reservations and special dining experiences when you add your eligible American Express® Card to your Resy profile. Terms Apply.</div>
      </TranslationProvider>
    )
  );

  const setTaglineCopy = () => {
    if (showGdaAccountPageCopy) {
      return (
        <TranslationProvider className="GdaOnboarding__Tagline GdaOnboarding__Tagline--large-font" translationKey="onboarding_personal_concierge_member_account_page">
          <div role="heading" aria-level={2}>Special booking access, just for you.</div>
        </TranslationProvider>
      );
    }
    if (showGdaVenuePageCopy) {
      return (
        <TranslationProvider className="GdaOnboarding__Tagline GdaOnboarding__Tagline--large-font" translationKey="onboarding_personal_concierge_member">
          <div>You have special access to reservations at this venue.</div>
        </TranslationProvider>
      );
    }

    return (
      <TranslationProvider className="GdaOnboarding__Tagline" translationKey="onboarding_personal_concierge_prospect">
        <div>Enrolled members get exclusive access to tables at GDA venues.</div>
      </TranslationProvider>
    );
  };

  const renderCardImage = () => (
    <img src={platinumCardImg} className="GdaOnboarding__platinum-card-image" alt="The Platinum Card®" />
  );

  const renderBenefitLink = () => (
    <div className="GdaOnboarding__benefit-link">
      <Link href="/global-dining-access" target="_blank">
        <TranslationProvider translationKey="onboarding_benefit_terms">
          <span>Benefit Terms &rsaquo;</span>
        </TranslationProvider>
      </Link>
    </div>
  );

  const onLearnMoreClick = () => {
    if (!isGda) {
      const eventProperties = {
        context: pageContext,
      };
      if (pageContext === VENUE_PAGE) {
        eventProperties.value = 'venue badge';
      }
      Analytics?.event('click gda link', eventProperties);
    }
    closeModal();
  };

  const renderPrimaryCta = () => (
    isGda ? (
      <Link className="GdaOnboarding__CTA" variant="gda-button-primary" onClick={closeModal}>
        <TranslationProvider translationKey="button_done">
          <div>Done</div>
        </TranslationProvider>
      </Link>
    ) : (
      <Link className="GdaOnboarding__CTA-prospect" variant="gda-button-primary" href="/global-dining-access" onClick={onLearnMoreClick}>
        <TranslationProvider translationKey="learn_more">
          <div>Learn More</div>
        </TranslationProvider>
      </Link>
    )
  );

  const renderGDALogo = () => {
    if (isMobile && isGda) {
      return (
        <div>
          <ResyGdaLogoMobile
            className="GdaOnboarding__Logo GdaOnboarding__Logo--Secondary"
            role="img"
            aria-label="Global Dining Access by Resy logo"
          />
        </div>
      );
    }

    return (
      <ResyGdaLogo
        className="GdaOnboarding__Logo GdaOnboarding__Logo--Primary"
        role="img"
        aria-label="Global Dining Access by Resy logo"
      />
    );
  };

  return (
    <div className="GdaOnboarding">
      <Button onClick={closeModal} variant="no-style" className={cx('GdaOnboarding__Close', { 'GdaOnboarding--hide': hideCta })} data-test-id="gda-onboarding-button-close">
        <Close />
      </Button>

      {renderGDALogo()}

      <div className={`GdaOnboarding__Header ${isMobile ? 'GdaOnboarding__Header--border-bottom' : 'GdaOnboarding__Header--border-top'} `}>
        <div className="GdaOnboarding__Card-Wrapper">
          { !isGda && (renderCardImage())}
          <div className="GdaOnboarding__Copy-Wrapper">
            {setTaglineCopy()}
            {renderHeader()}
          </div>
        </div>
      </div>
      <div className="GdaOnboarding__Info">
        <Exclusive />
        <PriorityNotify />
        <SpecialAccess />
        <VIPStatus />
      </div>

      { isGda && (renderBenefitLink())}

      <div
        className={cx('GdaOnboarding__CTA-container', {
          'GdaOnboarding--hide': hideCta,
          'GdaOnboarding__CTA-container--prospect': !isGda,
        })}
      >
        {renderPrimaryCta()}
      </div>
    </div>
  );
};

GdaOnboarding.propTypes = {
  closeModal: PropTypes.func,
  hideCta: PropTypes.bool,
  isGdaUser: PropTypes.bool,
  isMobile: PropTypes.bool,
};

GdaOnboarding.defaultProps = {
  closeModal: () => {},
  hideCta: false,
  isGdaUser: false,
  isMobile: false,
};

GdaOnboarding.displayName = 'GdaOnboarding';
const mapStateToProps = (state) => {
  const { matchMedia: { md } } = state;
  return {
    isGdaUser: selectIsGlobalDiningAccess(state),
    isMobile: !md,
  };
};

export { mapStateToProps, GdaOnboarding as ComponentUnderTest };
export default connect(mapStateToProps)(GdaOnboarding);
